import { ActionTree } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import SocialLoginState from '../types/SocialLoginState'
import { isServer, processURLAddress } from '@vue-storefront/core/helpers';
import config from 'config'
import rootStore from '@vue-storefront/core/store'
import fetch from 'isomorphic-fetch'
import i18n from '@vue-storefront/i18n'
import Vue from 'vue'
import { markFacebookSession } from '../helpers/facebook-sdk'
import { withRefreshTokenCookie } from '@vue-storefront/core/modules/user/helpers/refreshTokenCookie'
import { startLoginGeneration } from '@vue-storefront/core/modules/user/helpers/customerSession'

declare global {
  interface Window { FB: any, fbAsyncInit: any }
}

// ms-api FacebookEmailUnavailable reasons (K2-3168).
const FACEBOOK_EMAIL_DECLINED = 'facebook_email_declined'
const FACEBOOK_EMAIL_MISSING = 'facebook_email_missing'

// messageKey is an untranslated i18n key; options may set action1/action2/hasNoTimeout.
function facebookError (messageKey: string, options: any = {}) {
  Vue.prototype.$bus.$emit('notification-progress-stop')
  rootStore.dispatch('notification/spawnNotification', {
    type: 'error',
    message: i18n.t(messageKey),
    action1: { label: i18n.t('OK') },
    ...options
  })
}

async function facebookFailureReason (resp): Promise<string | null> {
  try {
    const body = await resp.json()
    return (body && body.result && typeof body.result.reason === 'string') ? body.result.reason : null
  } catch (e) {
    return null
  }
}

const actions: ActionTree<SocialLoginState, RootState> = {
  googleAuthenticate({ dispatch, commit }, token: string) {
    Vue.prototype.$bus.$emit('notification-progress-start', i18n.t('Authorization in progress ...'))
    let quoteId = rootStore.getters['cart/getCartToken']
    let url = processURLAddress(`${config.users.socialLoginGoogle_endpoint}?quoteId=${quoteId}`)
    fetch(url, withRefreshTokenCookie({ // K2-3138: refresh token comes back as the rut cookie
      method: 'POST',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    }))
      .then(resp => {
        if (resp.status !== 200) {
          Vue.prototype.$bus.$emit('notification-progress-stop');
          rootStore.dispatch('notification/spawnNotification', {
            type: 'error',
            message: i18n.t('Error while login with Google'),
            action1: { label: i18n.t('OK') }
          });
          throw new Error(`Network response code: ${resp.status}`);
        } else {
          return resp.json();
        }
      })
      .then(response => dispatch('login', response))
      .catch(error => {
        Vue.prototype.$bus.$emit('notification-progress-stop');
        rootStore.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t('Error while login with Google'),
          action1: { label: i18n.t('OK') }
        });
        console.error('Google authentication error: ', error);
      });
  },

  // K2-3168: the access token goes in the body; ms-api's 422 reason picks the message, and a
  // declined email permission can be asked for again (auth_type=rerequest). The payload is
  // a click event when bound straight to a button.
  async facebookAuthenticate ({ dispatch }, payload?: { rerequest?: boolean }) {
    if (!window.FB) {
      facebookError('Error while login with Facebook')
      console.error('Facebook authentication error: SDK is not loaded yet');
      return false;
    }
    const loginOptions = payload && payload.rerequest === true
      ? { scope: 'email', auth_type: 'rerequest' }
      : { scope: 'email' }
    Vue.prototype.$bus.$emit('notification-progress-start', i18n.t('Authorization in progress ...'))
    window.FB.login((response) => {
      if (!response.authResponse) {
        console.error('User cancelled login or did not fully authorize.')
        facebookError('Error while login with Facebook')
        return
      }
      let quoteId = rootStore.getters['cart/getCartToken']
      let url = processURLAddress(`${config.users.socialLoginFacebook_endpoint}?quoteId=${quoteId}`)
      fetch(url, withRefreshTokenCookie({ // K2-3138
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accessToken: window.FB.getAccessToken() })
      }))
        .then(async resp => {
          if (resp.status === 200) {
            const body = await resp.json()
            markFacebookSession()
            return dispatch('login', body)
          }
          const reason = await facebookFailureReason(resp)
          if (reason === FACEBOOK_EMAIL_DECLINED) {
            return facebookError('Facebook did not share your email with us. Allow access to your email to sign in.', {
              action1: { label: i18n.t('Try again'), action: () => dispatch('facebookAuthenticate', { rerequest: true }) },
              action2: { label: i18n.t('Cancel'), action: 'close' },
              hasNoTimeout: true
            })
          }
          if (reason === FACEBOOK_EMAIL_MISSING) {
            return facebookError('Your Facebook account has no confirmed email. Add an email in your Facebook settings, or sign in with Google or by email.', { hasNoTimeout: true })
          }
          facebookError('Error while login with Facebook')
          console.error(`Facebook authentication error: response code ${resp.status}`)
        })
        .catch(error => {
          facebookError('Error while login with Facebook')
          console.error('Facebook authentication error: ', error);
        });
    }, loginOptions);
    return false;
  },

  login ({}, customerInfo) {
    let customer = customerInfo.customer, token = customerInfo.token
    try {
      if (!token) {
        token = customerInfo.result
      }
    } catch (e) {
      console.error('Unable to retrieve tokens from response')
    }
    if (!isServer) {
      localStorage.setItem('shop/user/current-user', JSON.stringify(customer));
    }
    rootStore.state.user.token = token
    startLoginGeneration() // K2-3138: a new session, as user/login
    rootStore.state.userTokenInvalidateLock = 0
    rootStore.dispatch('user/me', { refresh: navigator.onLine }, { root: true })
      .then(user => {
        Vue.prototype.$bus.$emit('modal-hide', 'modal-signup');
        Vue.prototype.$bus.$emit('notification-progress-stop');
        Vue.prototype.$bus.$emit('success-authenticate');
      })
  }
}

export default actions
